package fyi.moko.hotpl.ui

import androidx.compose.foundation.gestures.detectHorizontalDragGestures
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.width
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.dp

/**
 * Whether the platform already provides an OS-level left-edge swipe-back gesture
 * that this Compose overlay would duplicate.
 *
 * - Android: the system predictive-back gesture owns the left-edge swipe, so the
 *   overlay is disabled there to avoid fighting the OS.
 * - iOS: Compose Multiplatform navigation does not wire
 *   `UINavigationController.interactivePopGestureRecognizer`, so we provide an
 *   edge-swipe here. See `docs/archive/IOS_PORTING_PLAN.md` §8.4.
 */
expect val platformSupportsEdgeSwipeBack: Boolean

/**
 * A left-edge, drag-right-to-go-back gesture for iOS, layered over the nav host.
 *
 * - Only a drag that *starts* within the leftmost [edgeWidthDp] begins a gesture,
 *   so map pans / list scrolls that start elsewhere are untouched.
 * - A tap (down + up, no drag) is not consumed, so controls sitting at the left
 *   edge (e.g. the Back button) still receive their taps.
 * - Fires [onBack] once the cumulative rightward drag exceeds [thresholdDp].
 *
 * No-op when [enabled] is false (e.g. on the start destination or on Android).
 */
@Composable
fun EdgeSwipeBackGesture(
    onBack: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    edgeWidthDp: Float = 24f,
    thresholdDp: Float = 40f,
) {
    if (!enabled) return
    val density = LocalDensity.current
    val thresholdPx = with(density) { thresholdDp.dp.toPx() }
    var totalDrag by remember { mutableFloatStateOf(0f) }

    Box(
        modifier = modifier
            .fillMaxHeight()
            .width(edgeWidthDp.dp)
            .pointerInput(enabled) {
                detectHorizontalDragGestures(
                    onDragStart = { totalDrag = 0f },
                    onDragEnd = {
                        if (totalDrag > thresholdPx) onBack()
                        totalDrag = 0f
                    },
                    onDragCancel = { totalDrag = 0f },
                    onHorizontalDrag = { change, dragAmount ->
                        change.consume()
                        totalDrag += dragAmount
                    },
                )
            },
    )
}